/* eslint-env browser */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';
import ClientSideConfigSelector from '../../../../recoil/cashout/ClientSideConfigSelector';
import IFrame from '../common/IFrame';
import Loading from '../../../general/Loading';
import useTaxAccount, { TaxFormErrorCode } from '../hooks/useTaxAccount';

export default function CashoutTaxForm({ form, onContinue, onClose }) {
  const { refreshTaxAccount, requestTaxForm } = useTaxAccount();

  const config = useRecoilValue(ClientSideConfigSelector);
  const {
    live: {
      claimReward: {
        paymentProvider,
        hideDownloadTaxFormButton = false,
      } = {},
    } = {},
  } = config || {};

  const [processing, setProcessing] = useState(false);
  const [registrationUrl, setRegistrationUrl] = useState(null);
  const [showContinueButton, setShowContinueButton] = useState(false);

  const {
    formUrl,
    requestForce,
  } = form || {};

  const handleIframeClose = async (callback) => {
    if (processing) {
      return;
    }

    setProcessing(true);
    try {
      await refreshTaxAccount();
      await callback();
    } catch (err) {
      console.log('Unable to refresh tax form status', err && err.responseJSON);
    } finally {
      setProcessing(false);
    }
  };

  const taxBanditsPostMessageHandler = async (evt) => {
    const {
      uid,
      iat,
      Status: status,
      PayeeRef: payeeRef,
      FormType: formType,
    } = evt.data;

    if (status === 'Signed') {
      setShowContinueButton(true);
    } else {
      console.log(`Invalid form status of ${status}!`, uid, iat, payeeRef, formType);
    }
  };

  const postMessageEventHandlerMap = {
    taxBandits: {
      domainTest: /taxbandits.io$/,
      handler: taxBanditsPostMessageHandler,
    },
  };

  // todo: need to refactor this part
  const getPostMessageHandler = (evt) => {
    const match = Object.values(postMessageEventHandlerMap)
      .find(({ domainTest }) => domainTest.test(evt.origin));

    return match ? match.handler : null;
  };

  const buildRegistrationUrl = (url) => {
    const formRegistrationUrl = new URL(url, window.location.origin);

    if (hideDownloadTaxFormButton) {
      formRegistrationUrl.searchParams.append('hb', 'true');
    }

    return formRegistrationUrl.toString();
  };

  useEffect(() => {
    const listener = (evt) => {
      const handler = getPostMessageHandler(evt);
      if (handler) {
        handler(evt);
      } else {
        console.log(`Rejected message from unknown domain ${evt.domain}`);
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationUrl]);

  useEffect(() => {
    const getRegistrationUrl = async () => {
      if (registrationUrl) return; // Guard to prevent a requesting a new form

      if (formUrl) {
        setRegistrationUrl(buildRegistrationUrl(formUrl));
        return;
      }

      try {
        const result = await requestTaxForm(requestForce);

        setRegistrationUrl(buildRegistrationUrl(result.url));
      } catch (err) {
        const errorData = err?.json ? await err.json() : {};

        if (errorData?.type === TaxFormErrorCode.AlreadyStarted) {
          const url = errorData?.data?.url || errorData?.url;
          if (url) {
            setRegistrationUrl(buildRegistrationUrl(url));
            return;
          }
        }

        if (errorData?.type === TaxFormErrorCode.AlreadyAccepted) {
          await refreshTaxAccount();
          await onClose();
          return;
        }

        throw new Error('Error during requestion a tax form...');
      }
    };

    getRegistrationUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProvider, formUrl, requestForce]);

  if (!registrationUrl) {
    return <Loading />;
  }

  return (
    <IFrame
      title=""
      url={registrationUrl}
      showLoading={processing}
      showContinueButton={showContinueButton}
      onClose={() => handleIframeClose(onClose)}
      onContinue={() => handleIframeClose(onContinue)}
    />
  );
}
CashoutTaxForm.propTypes = {
  form: PropTypes.shape({
    formUrl: PropTypes.string,
    requestForce: PropTypes.bool,
  }),
  onContinue: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
CashoutTaxForm.defaultProps = {
  form: {
    formUrl: undefined,
    requestForce: false,
  },
};
